import {
  CEDAR_BUNKER_POINT_COUNT,
  CEDAR_BUNKER_LAYOUTS,
  CEDAR_GROUND_SIZE_FT,
  CEDAR_GREEN_LAYOUTS,
  CEDAR_TERRAIN_TRIANGLE_COUNT,
  CEDAR_TERRAIN_VERTEX_COUNT,
  CEDAR_TERRAIN_X_COORDS,
  CEDAR_TERRAIN_Z_COORDS,
  CEDAR_TREE_ALPHA_TEST,
  CEDAR_TREE_GROUND_OVERLAP_FT,
  cedarBunkerOpeningField,
  cedarGreenRadiusForHole,
  cedarGreenZoneDistance,
  cedarRenderedTerrainHeightAt,
  cedarTerrainHeightAt,
  cedarTreeAtlasBaselineInsets,
  cedarTreeCardCenterY,
  cedarTreeSpecsForHole,
  resolveCedarBunkers,
  type CedarBunkerSpec,
  type CedarFlatPath,
  type CedarGreenLayout,
  type CedarGreenSize,
  type CedarTreeSpec,
  type ResolvedCedarBunker,
} from '../cedarVisuals';

export const REALISTIC_SCENERY_IDS = [
  'cedar',
  'island',
  'desert',
  'links',
  'city',
  'alpine',
  'pine',
  'autumn',
  'twilight',
  'storm',
] as const;

export type RealisticSceneryId = (typeof REALISTIC_SCENERY_IDS)[number];

export type RealisticGreenSize = CedarGreenSize;
export type RealisticGreenLayout = CedarGreenLayout;
export type RealisticBunkerSpec = CedarBunkerSpec;
export type ResolvedRealisticBunker = ResolvedCedarBunker;
export type RealisticFlatPath = CedarFlatPath;
export type RealisticVegetationSpec = CedarTreeSpec;

// The generic renderer deliberately reuses Cedar's proven mobile geometry
// budget and grounding math. These aliases let it avoid Cedar-specific names.
export const REALISTIC_BUNKER_POINT_COUNT = CEDAR_BUNKER_POINT_COUNT;
export const REALISTIC_GROUND_SIZE_FT = CEDAR_GROUND_SIZE_FT;
export const REALISTIC_TERRAIN_X_COORDS = CEDAR_TERRAIN_X_COORDS;
export const REALISTIC_TERRAIN_Z_COORDS = CEDAR_TERRAIN_Z_COORDS;
export const REALISTIC_TERRAIN_VERTEX_COUNT = CEDAR_TERRAIN_VERTEX_COUNT;
export const REALISTIC_TERRAIN_TRIANGLE_COUNT = CEDAR_TERRAIN_TRIANGLE_COUNT;
export const REALISTIC_VEGETATION_ALPHA_TEST = CEDAR_TREE_ALPHA_TEST;
export const REALISTIC_VEGETATION_GROUND_OVERLAP_FT = CEDAR_TREE_GROUND_OVERLAP_FT;
export const realisticVegetationAtlasBaselineInsets = cedarTreeAtlasBaselineInsets;
export const realisticVegetationCardCenterY = cedarTreeCardCenterY;

export interface RealisticPalette {
  /** Mild multipliers for the PBR turf maps rather than flat material colors. */
  greenTint: number;
  roughTint: readonly [number, number, number];
  fringeTint: readonly [number, number, number];
  bunkerSand: number;
  bunkerWall: number;
  bunkerShadow: number;
  vegetationTint: number;
}

export type RealisticTerrainKind =
  | 'ridge'
  | 'coastal'
  | 'mesa'
  | 'dunes'
  | 'urban-park'
  | 'alpine'
  | 'pine-ridge'
  | 'glen'
  | 'municipal'
  | 'storm-coast';

export type RealisticAtlasKey =
  | 'cedar'
  | 'tropical'
  | 'desert'
  | 'links'
  | 'parkland'
  | 'twilight'
  | 'autumn'
  | 'storm';

export interface RealisticTerrainProfile {
  kind: RealisticTerrainKind;
  lobeCount: number;
  /** Final clamps make the topology safe even when several lobes overlap. */
  maxRiseFt: number;
  maxDepressionFt: number;
  riseBaseFt: number;
  riseJitterFt: number;
  depressionBaseFt: number;
  radialOffsetFt: number;
  radialJitterFt: number;
  spreadXBaseFt: number;
  spreadXJitterFt: number;
  spreadZBaseFt: number;
  spreadZJitterFt: number;
  /** Full relief is gone before the terrain grid becomes sparse. */
  edgeFadeStartFt: number;
  edgeFadeEndFt: number;
  /** Distance beyond the green radius where surrounding relief reaches 100%. */
  fullReliefOffsetFt: number;
}

export interface RealisticVegetationProfile {
  /** One billboard is two triangles; all courses remain at or below 64 cards. */
  cardCount: number;
  nearCardsPerSide: number;
  corridorHalfWidthFt: number;
  nearMinXFt: number;
  nearXJitterFt: number;
  nearStartZFt: number;
  nearStepZFt: number;
  horizonRadiusFt: number;
  horizonDepthJitterFt: number;
  minHeightFt: number;
  heightJitterFt: number;
  variantWidthRatios: readonly [number, number, number, number];
  toneJitter: number;
}

export interface RealisticBunkerProfile {
  countsByHole: Readonly<Record<number, number>>;
  widthScale: number;
  lengthScale: number;
  minAlong: number;
  maxAlong: number;
  depthFt: number;
}

export interface RealisticCourseVisualSpec {
  id: RealisticSceneryId;
  /** Stable art seed. Changing it is a visible course-layout change. */
  seed: number;
  /** Shared production atlas resolved through a static runtime require map. */
  atlasKey: RealisticAtlasKey;
  palette: RealisticPalette;
  greenLayouts: Readonly<Record<number, RealisticGreenLayout>>;
  bunkers: RealisticBunkerProfile;
  terrain: RealisticTerrainProfile;
  vegetation: RealisticVegetationProfile;
}

const HOLES = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

function holeRecord<T>(values: readonly T[]): Readonly<Record<number, T>> {
  if (values.length !== 9) throw new Error('realistic course specs require exactly nine holes');
  return Object.freeze(
    Object.fromEntries(HOLES.map((hole, index) => [hole, values[index]]))
  ) as Readonly<Record<number, T>>;
}

function greenLayouts(
  sizes: readonly RealisticGreenSize[],
  margins: Readonly<Record<RealisticGreenSize, number>>
): Readonly<Record<number, RealisticGreenLayout>> {
  return holeRecord(
    sizes.map((size) => Object.freeze({ size, addressMarginFt: margins[size] }))
  );
}

function bunkerCounts(values: readonly number[]): Readonly<Record<number, number>> {
  return holeRecord(values);
}

const CEDAR_TERRAIN: RealisticTerrainProfile = Object.freeze({
  kind: 'ridge',
  lobeCount: 6,
  maxRiseFt: 2.25,
  maxDepressionFt: 0.65,
  riseBaseFt: 0.72,
  riseJitterFt: 1.04,
  depressionBaseFt: 0.58,
  radialOffsetFt: 34,
  radialJitterFt: 47,
  spreadXBaseFt: 29,
  spreadXJitterFt: 19,
  spreadZBaseFt: 35,
  spreadZJitterFt: 23,
  edgeFadeStartFt: 150,
  edgeFadeEndFt: 205,
  fullReliefOffsetFt: 39,
});

const CEDAR_VEGETATION: RealisticVegetationProfile = Object.freeze({
  cardCount: 64,
  nearCardsPerSide: 10,
  corridorHalfWidthFt: 18,
  nearMinXFt: 50,
  nearXJitterFt: 30,
  nearStartZFt: -42,
  nearStepZFt: 12,
  horizonRadiusFt: 168,
  horizonDepthJitterFt: 62,
  minHeightFt: 17,
  heightJitterFt: 16,
  variantWidthRatios: [0.2, 0.2, 0.25, 0.2] as const,
  toneJitter: 0.08,
});

const CEDAR_BUNKER_COUNTS = Object.freeze(
  Object.fromEntries(HOLES.map((hole) => [hole, CEDAR_BUNKER_LAYOUTS[hole].length]))
) as Readonly<Record<number, number>>;

/**
 * Course 11 remains the reference implementation. Courses 12-20 only vary
 * data in this table, so adding scenery does not multiply draw-call code.
 */
export const REALISTIC_VISUAL_SPECS: Readonly<
  Record<RealisticSceneryId, RealisticCourseVisualSpec>
> = Object.freeze({
  cedar: {
    id: 'cedar',
    seed: 0x00c3da12,
    atlasKey: 'cedar',
    palette: {
      greenTint: 0xd7ffd7,
      roughTint: [0.49, 0.68, 0.47],
      fringeTint: [0.71, 0.85, 0.63],
      bunkerSand: 0xc6aa72,
      bunkerWall: 0xa98756,
      bunkerShadow: 0x725c3f,
      vegetationTint: 0xffffff,
    },
    // Keep the exact object and helpers used by the established course.
    greenLayouts: CEDAR_GREEN_LAYOUTS,
    bunkers: {
      countsByHole: CEDAR_BUNKER_COUNTS,
      widthScale: 1,
      lengthScale: 1,
      minAlong: 0.25,
      maxAlong: 0.88,
      depthFt: 0.18,
    },
    terrain: CEDAR_TERRAIN,
    vegetation: CEDAR_VEGETATION,
  },
  island: {
    id: 'island',
    seed: 0x12a11e51,
    atlasKey: 'tropical',
    palette: {
      greenTint: 0xd4f3cc,
      roughTint: [0.56, 0.63, 0.39],
      fringeTint: [0.76, 0.82, 0.55],
      bunkerSand: 0xe3d3a2,
      bunkerWall: 0xc4aa78,
      bunkerShadow: 0x927a55,
      vegetationTint: 0xf8fff3,
    },
    greenLayouts: greenLayouts(
      ['compact', 'medium', 'large', 'compact', 'large', 'medium', 'large', 'compact', 'medium'],
      { compact: 5, medium: 11, large: 18 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([1, 2, 0, 1, 3, 1, 2, 0, 2]),
      widthScale: 1.08,
      lengthScale: 1.05,
      minAlong: 0.25,
      maxAlong: 0.86,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'coastal', lobeCount: 5, maxRiseFt: 1.65, maxDepressionFt: 0.42,
      riseBaseFt: 0.46, riseJitterFt: 0.7, depressionBaseFt: 0.34,
      radialOffsetFt: 25, radialJitterFt: 54, spreadXBaseFt: 38, spreadXJitterFt: 22,
      spreadZBaseFt: 30, spreadZJitterFt: 20, edgeFadeStartFt: 155, edgeFadeEndFt: 210,
      fullReliefOffsetFt: 31,
    },
    vegetation: {
      cardCount: 48, nearCardsPerSide: 7, corridorHalfWidthFt: 20,
      nearMinXFt: 56, nearXJitterFt: 38, nearStartZFt: -48, nearStepZFt: 15,
      horizonRadiusFt: 182, horizonDepthJitterFt: 70, minHeightFt: 18, heightJitterFt: 17,
      variantWidthRatios: [0.55, 0.62, 0.72, 0.78], toneJitter: 0.07,
    },
  },
  desert: {
    id: 'desert',
    seed: 0x0de5e247,
    atlasKey: 'desert',
    palette: {
      greenTint: 0xd4efd0,
      roughTint: [1.28, 0.70, 0.48],
      fringeTint: [0.78, 0.74, 0.5],
      bunkerSand: 0xd7b477,
      bunkerWall: 0xb57b4d,
      bunkerShadow: 0x714731,
      vegetationTint: 0xfff3d7,
    },
    greenLayouts: greenLayouts(
      ['medium', 'large', 'compact', 'large', 'medium', 'compact', 'compact', 'medium', 'large'],
      { compact: 6, medium: 13, large: 21 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([0, 2, 1, 3, 1, 2, 0, 3, 1]),
      widthScale: 1.14,
      lengthScale: 0.95,
      minAlong: 0.24,
      maxAlong: 0.84,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'mesa', lobeCount: 5, maxRiseFt: 2.65, maxDepressionFt: 0.55,
      riseBaseFt: 0.9, riseJitterFt: 1.15, depressionBaseFt: 0.43,
      radialOffsetFt: 35, radialJitterFt: 50, spreadXBaseFt: 42, spreadXJitterFt: 28,
      spreadZBaseFt: 33, spreadZJitterFt: 18, edgeFadeStartFt: 170, edgeFadeEndFt: 225,
      fullReliefOffsetFt: 33,
    },
    vegetation: {
      cardCount: 36, nearCardsPerSide: 5, corridorHalfWidthFt: 24,
      nearMinXFt: 62, nearXJitterFt: 50, nearStartZFt: -50, nearStepZFt: 18,
      horizonRadiusFt: 205, horizonDepthJitterFt: 85, minHeightFt: 9, heightJitterFt: 18,
      variantWidthRatios: [0.35, 0.52, 0.72, 0.82], toneJitter: 0.06,
    },
  },
  links: {
    id: 'links',
    seed: 0x11a9f3e2,
    atlasKey: 'links',
    palette: {
      greenTint: 0xdee5c5,
      roughTint: [0.58, 0.58, 0.34],
      fringeTint: [0.74, 0.76, 0.5],
      bunkerSand: 0xd8c89b,
      bunkerWall: 0xb6a16d,
      bunkerShadow: 0x77684a,
      vegetationTint: 0xf1f3df,
    },
    greenLayouts: greenLayouts(
      ['large', 'medium', 'compact', 'medium', 'large', 'compact', 'compact', 'large', 'medium'],
      { compact: 5, medium: 12, large: 20 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([2, 3, 1, 0, 2, 3, 1, 2, 0]),
      widthScale: 1.22,
      lengthScale: 0.92,
      minAlong: 0.62,
      maxAlong: 0.92,
      depthFt: 0.88,
    },
    terrain: {
      kind: 'dunes', lobeCount: 7, maxRiseFt: 2.05, maxDepressionFt: 0.5,
      riseBaseFt: 0.5, riseJitterFt: 0.65, depressionBaseFt: 0.38,
      radialOffsetFt: 24, radialJitterFt: 66, spreadXBaseFt: 48, spreadXJitterFt: 30,
      spreadZBaseFt: 26, spreadZJitterFt: 18, edgeFadeStartFt: 165, edgeFadeEndFt: 220,
      fullReliefOffsetFt: 30,
    },
    vegetation: {
      cardCount: 40, nearCardsPerSide: 5, corridorHalfWidthFt: 22,
      nearMinXFt: 58, nearXJitterFt: 44, nearStartZFt: -44, nearStepZFt: 16,
      horizonRadiusFt: 188, horizonDepthJitterFt: 78, minHeightFt: 6, heightJitterFt: 11,
      variantWidthRatios: [0.92, 0.82, 0.68, 0.74], toneJitter: 0.08,
    },
  },
  city: {
    id: 'city',
    seed: 0x0c17f5a9,
    atlasKey: 'parkland',
    palette: {
      greenTint: 0xd7ead2,
      roughTint: [0.45, 0.51, 0.35],
      fringeTint: [0.7, 0.76, 0.53],
      bunkerSand: 0xcab989,
      bunkerWall: 0xa38e62,
      bunkerShadow: 0x655b4a,
      vegetationTint: 0xe8edf0,
    },
    greenLayouts: greenLayouts(
      ['compact', 'large', 'medium', 'medium', 'compact', 'large', 'large', 'medium', 'compact'],
      { compact: 4.5, medium: 9, large: 15 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([0, 1, 2, 0, 1, 2, 0, 1, 2]),
      widthScale: 0.86,
      lengthScale: 0.82,
      minAlong: 0.3,
      maxAlong: 0.8,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'urban-park', lobeCount: 4, maxRiseFt: 1.25, maxDepressionFt: 0.35,
      riseBaseFt: 0.36, riseJitterFt: 0.4, depressionBaseFt: 0.28,
      radialOffsetFt: 31, radialJitterFt: 36, spreadXBaseFt: 34, spreadXJitterFt: 18,
      spreadZBaseFt: 40, spreadZJitterFt: 20, edgeFadeStartFt: 145, edgeFadeEndFt: 195,
      fullReliefOffsetFt: 32,
    },
    vegetation: {
      cardCount: 32, nearCardsPerSide: 4, corridorHalfWidthFt: 20,
      nearMinXFt: 60, nearXJitterFt: 42, nearStartZFt: -54, nearStepZFt: 20,
      horizonRadiusFt: 195, horizonDepthJitterFt: 60, minHeightFt: 22, heightJitterFt: 31,
      variantWidthRatios: [0.42, 0.5, 0.58, 0.66], toneJitter: 0.04,
    },
  },
  alpine: {
    id: 'alpine',
    seed: 0x0a1f1e83,
    atlasKey: 'cedar',
    palette: {
      greenTint: 0xc9ebd3,
      roughTint: [0.45, 0.52, 0.36],
      fringeTint: [0.72, 0.78, 0.55],
      bunkerSand: 0xd2c7a8,
      bunkerWall: 0xa99d7f,
      bunkerShadow: 0x6f6d65,
      vegetationTint: 0xf2f7f5,
    },
    greenLayouts: greenLayouts(
      ['medium', 'compact', 'large', 'large', 'medium', 'compact', 'medium', 'large', 'compact'],
      { compact: 6, medium: 12, large: 19 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([1, 0, 2, 1, 3, 0, 2, 1, 2]),
      widthScale: 0.94,
      lengthScale: 1.05,
      minAlong: 0.26,
      maxAlong: 0.85,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'alpine', lobeCount: 6, maxRiseFt: 2.8, maxDepressionFt: 0.72,
      riseBaseFt: 0.88, riseJitterFt: 1.2, depressionBaseFt: 0.58,
      radialOffsetFt: 31, radialJitterFt: 52, spreadXBaseFt: 31, spreadXJitterFt: 18,
      spreadZBaseFt: 38, spreadZJitterFt: 30, edgeFadeStartFt: 170, edgeFadeEndFt: 225,
      fullReliefOffsetFt: 33,
    },
    vegetation: {
      cardCount: 58, nearCardsPerSide: 8, corridorHalfWidthFt: 19,
      nearMinXFt: 52, nearXJitterFt: 34, nearStartZFt: -40, nearStepZFt: 13,
      horizonRadiusFt: 175, horizonDepthJitterFt: 74, minHeightFt: 18, heightJitterFt: 19,
      variantWidthRatios: [0.22, 0.28, 0.32, 0.45], toneJitter: 0.06,
    },
  },
  pine: {
    id: 'pine',
    seed: 0x0719e4a1,
    atlasKey: 'cedar',
    palette: {
      greenTint: 0xd5efd0,
      roughTint: [0.42, 0.5, 0.34],
      fringeTint: [0.68, 0.75, 0.51],
      bunkerSand: 0xc9b680,
      bunkerWall: 0xa18a5d,
      bunkerShadow: 0x62513b,
      vegetationTint: 0xedf4e9,
    },
    greenLayouts: greenLayouts(
      ['large', 'compact', 'medium', 'medium', 'large', 'compact', 'compact', 'medium', 'large'],
      { compact: 5, medium: 11, large: 18 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([2, 1, 0, 2, 1, 3, 1, 0, 2]),
      widthScale: 1,
      lengthScale: 1.08,
      minAlong: 0.24,
      maxAlong: 0.87,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'pine-ridge', lobeCount: 6, maxRiseFt: 2.35, maxDepressionFt: 0.62,
      riseBaseFt: 0.7, riseJitterFt: 0.92, depressionBaseFt: 0.5,
      radialOffsetFt: 28, radialJitterFt: 48, spreadXBaseFt: 30, spreadXJitterFt: 22,
      spreadZBaseFt: 40, spreadZJitterFt: 25, edgeFadeStartFt: 155, edgeFadeEndFt: 210,
      fullReliefOffsetFt: 31,
    },
    vegetation: {
      cardCount: 64, nearCardsPerSide: 10, corridorHalfWidthFt: 18,
      nearMinXFt: 48, nearXJitterFt: 32, nearStartZFt: -40, nearStepZFt: 12,
      horizonRadiusFt: 166, horizonDepthJitterFt: 64, minHeightFt: 19, heightJitterFt: 17,
      variantWidthRatios: [0.2, 0.23, 0.27, 0.3], toneJitter: 0.07,
    },
  },
  autumn: {
    id: 'autumn',
    seed: 0x0a879f31,
    atlasKey: 'autumn',
    palette: {
      greenTint: 0xe7edc8,
      roughTint: [0.56, 0.5, 0.3],
      fringeTint: [0.76, 0.72, 0.47],
      bunkerSand: 0xcfb982,
      bunkerWall: 0xa38459,
      bunkerShadow: 0x674a35,
      vegetationTint: 0xffe0bd,
    },
    greenLayouts: greenLayouts(
      ['medium', 'large', 'compact', 'compact', 'medium', 'large', 'large', 'compact', 'medium'],
      { compact: 6, medium: 13, large: 20 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([0, 2, 1, 3, 0, 2, 1, 2, 1]),
      widthScale: 1.06,
      lengthScale: 1.02,
      minAlong: 0.25,
      maxAlong: 0.86,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'glen', lobeCount: 6, maxRiseFt: 2.2, maxDepressionFt: 0.78,
      riseBaseFt: 0.62, riseJitterFt: 0.85, depressionBaseFt: 0.64,
      radialOffsetFt: 27, radialJitterFt: 46, spreadXBaseFt: 38, spreadXJitterFt: 24,
      spreadZBaseFt: 42, spreadZJitterFt: 28, edgeFadeStartFt: 160, edgeFadeEndFt: 215,
      fullReliefOffsetFt: 31,
    },
    vegetation: {
      cardCount: 60, nearCardsPerSide: 8, corridorHalfWidthFt: 19,
      nearMinXFt: 50, nearXJitterFt: 35, nearStartZFt: -43, nearStepZFt: 13,
      horizonRadiusFt: 172, horizonDepthJitterFt: 68, minHeightFt: 17, heightJitterFt: 18,
      variantWidthRatios: [0.48, 0.54, 0.5, 0.43], toneJitter: 0.08,
    },
  },
  twilight: {
    id: 'twilight',
    seed: 0x7a119617,
    atlasKey: 'twilight',
    palette: {
      greenTint: 0xdeecd1,
      roughTint: [0.38, 0.44, 0.32],
      fringeTint: [0.61, 0.67, 0.49],
      bunkerSand: 0xb8ad8d,
      bunkerWall: 0x8d8269,
      bunkerShadow: 0x49484b,
      vegetationTint: 0xc5d3dc,
    },
    greenLayouts: greenLayouts(
      ['compact', 'medium', 'large', 'large', 'compact', 'medium', 'medium', 'large', 'compact'],
      { compact: 4.5, medium: 10, large: 16 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([1, 0, 1, 2, 0, 3, 1, 2, 0]),
      widthScale: 0.9,
      lengthScale: 0.88,
      minAlong: 0.27,
      maxAlong: 0.82,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'municipal', lobeCount: 5, maxRiseFt: 1.5, maxDepressionFt: 0.46,
      riseBaseFt: 0.4, riseJitterFt: 0.52, depressionBaseFt: 0.36,
      radialOffsetFt: 30, radialJitterFt: 42, spreadXBaseFt: 40, spreadXJitterFt: 22,
      spreadZBaseFt: 36, spreadZJitterFt: 24, edgeFadeStartFt: 150, edgeFadeEndFt: 205,
      fullReliefOffsetFt: 32,
    },
    vegetation: {
      cardCount: 50, nearCardsPerSide: 7, corridorHalfWidthFt: 20,
      nearMinXFt: 54, nearXJitterFt: 40, nearStartZFt: -46, nearStepZFt: 15,
      horizonRadiusFt: 184, horizonDepthJitterFt: 68, minHeightFt: 18, heightJitterFt: 17,
      variantWidthRatios: [0.42, 0.5, 0.56, 0.46], toneJitter: 0.045,
    },
  },
  storm: {
    id: 'storm',
    seed: 0x57024e91,
    atlasKey: 'storm',
    palette: {
      greenTint: 0xd7e0c6,
      roughTint: [0.65, 0.63, 0.62],
      fringeTint: [0.64, 0.68, 0.5],
      bunkerSand: 0xb8b7ad,
      bunkerWall: 0x888784,
      bunkerShadow: 0x4f4e4a,
      vegetationTint: 0xbfc8c6,
    },
    greenLayouts: greenLayouts(
      ['large', 'medium', 'compact', 'compact', 'large', 'medium', 'medium', 'compact', 'large'],
      { compact: 6, medium: 14, large: 22 }
    ),
    bunkers: {
      countsByHole: bunkerCounts([3, 2, 1, 3, 2, 0, 3, 1, 2]),
      widthScale: 1.18,
      lengthScale: 1.1,
      minAlong: 0.22,
      maxAlong: 0.88,
      depthFt: 0.18,
    },
    terrain: {
      kind: 'storm-coast', lobeCount: 7, maxRiseFt: 2.5, maxDepressionFt: 0.82,
      riseBaseFt: 0.72, riseJitterFt: 1.05, depressionBaseFt: 0.68,
      radialOffsetFt: 25, radialJitterFt: 62, spreadXBaseFt: 44, spreadXJitterFt: 30,
      spreadZBaseFt: 31, spreadZJitterFt: 22, edgeFadeStartFt: 165, edgeFadeEndFt: 220,
      fullReliefOffsetFt: 30,
    },
    vegetation: {
      cardCount: 44, nearCardsPerSide: 6, corridorHalfWidthFt: 23,
      nearMinXFt: 58, nearXJitterFt: 46, nearStartZFt: -42, nearStepZFt: 17,
      horizonRadiusFt: 192, horizonDepthJitterFt: 82, minHeightFt: 13, heightJitterFt: 18,
      variantWidthRatios: [0.28, 0.38, 0.3, 0.72], toneJitter: 0.05,
    },
  },
});

export function isRealisticSceneryId(value: unknown): value is RealisticSceneryId {
  return typeof value === 'string' && (REALISTIC_SCENERY_IDS as readonly string[]).includes(value);
}

export function realisticSpecById(id: RealisticSceneryId): RealisticCourseVisualSpec {
  return REALISTIC_VISUAL_SPECS[id];
}

function clampedHole(holeNumber: number): number {
  return Math.max(1, Math.min(9, Math.round(holeNumber)));
}

export function realisticGreenLayoutForHole(
  sceneryId: RealisticSceneryId,
  holeNumber: number
): RealisticGreenLayout {
  const spec = realisticSpecById(sceneryId);
  const hole = clampedHole(holeNumber);
  return spec.greenLayouts[hole] ?? spec.greenLayouts[1];
}

export function realisticGreenRadiusForHole(
  sceneryId: RealisticSceneryId,
  holeNumber: number,
  targetFt: number
): number {
  if (sceneryId === 'cedar') return cedarGreenRadiusForHole(holeNumber, targetFt);
  return (
    Math.max(0, targetFt) * 0.55 +
    realisticGreenLayoutForHole(sceneryId, holeNumber).addressMarginFt
  );
}

/** Elliptical distance used by the realistic ground shader for every biome. */
export function realisticGreenZoneDistance(x: number, z: number, targetFt: number): number {
  return cedarGreenZoneDistance(x, z, targetFt);
}

const BUNKER_POINT_COUNT = 18;

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function bunkerOutline(
  bunker: RealisticBunkerSpec,
  targetFt: number,
  radiusScale: number
): Array<readonly [number, number]> {
  const centerZ = -targetFt * bunker.along;
  const cosR = Math.cos(bunker.rotation);
  const sinR = Math.sin(bunker.rotation);
  const points: Array<readonly [number, number]> = [];
  for (let point = 0; point < BUNKER_POINT_COUNT; point++) {
    const angle = (point / BUNKER_POINT_COUNT) * Math.PI * 2;
    const radius =
      radiusScale *
      (1 +
        0.16 * Math.sin(angle * 3 + bunker.seed * 0.17) +
        0.08 * Math.sin(angle * 5 + bunker.seed * 0.31));
    const localX = Math.cos(angle) * bunker.width * 0.5 * radius;
    const localZ = Math.sin(angle) * bunker.length * 0.5 * radius;
    points.push([
      bunker.x + localX * cosR - localZ * sinR,
      centerZ + localX * sinR + localZ * cosR,
    ]);
  }
  return points;
}

function minimumOutlineClearance(
  outline: ReadonlyArray<readonly [number, number]>,
  targetFt: number,
  roughRadius: number
): number {
  let minimum = Number.POSITIVE_INFINITY;
  for (let point = 0; point < outline.length; point++) {
    const start = outline[point];
    const end = outline[(point + 1) % outline.length];
    for (let sample = 0; sample <= 8; sample++) {
      const amount = sample / 8;
      const x = start[0] + (end[0] - start[0]) * amount;
      const z = start[1] + (end[1] - start[1]) * amount;
      minimum = Math.min(
        minimum,
        realisticGreenZoneDistance(x, z, targetFt) - roughRadius
      );
    }
  }
  return minimum;
}

export function resolveRealisticBunkers(
  sceneryId: RealisticSceneryId,
  holeNumber: number,
  targetFt: number
): ResolvedRealisticBunker[] {
  if (sceneryId === 'cedar') return resolveCedarBunkers(holeNumber, targetFt);

  const spec = realisticSpecById(sceneryId);
  const hole = clampedHole(holeNumber);
  const count = Math.max(0, Math.min(3, Math.round(spec.bunkers.countsByHole[hole] ?? 0)));
  const random = seededRandom(spec.seed ^ Math.imul(hole, 0x9e3779b1));
  const greenRadius = realisticGreenRadiusForHole(sceneryId, hole, targetFt);
  const roughRadius = greenRadius + 3.8;
  const greenCenterZ = -targetFt * 0.55;
  const result: ResolvedRealisticBunker[] = [];

  for (let index = 0; index < count; index++) {
    const side = (index + hole + (spec.seed & 1)) % 2 === 0 ? -1 : 1;
    const along =
      spec.bunkers.minAlong +
      random() * (spec.bunkers.maxAlong - spec.bunkers.minAlong);
    const width = (4.5 + random() * 3.1) * spec.bunkers.widthScale;
    const length = (2.35 + random() * 1.35) * spec.bunkers.lengthScale;
    const rotation = side * (0.16 + random() * 0.5);
    const seed = (spec.seed + hole * 101 + index * 17) >>> 0;
    const centerZ = -targetFt * along;
    const centerDeltaZ = centerZ - greenCenterZ;
    const sideEdge =
      Math.abs(centerDeltaZ) < roughRadius
        ? Math.sqrt(roughRadius * roughRadius - centerDeltaZ * centerDeltaZ) / 1.25
        : 0;
    const variedGap = 1.65 + random() * 1.35;
    const bunker: RealisticBunkerSpec = {
      id: `${side < 0 ? 'left' : 'right'}-${index + 1}`,
      x: side * (sideEdge + width * 0.5 + variedGap),
      along,
      width,
      length,
      rotation,
      seed,
    };

    let outer = bunkerOutline(bunker, targetFt, 1);
    // Irregular rotated corners can sit closer than the center-line estimate.
    // Shift sideways until every sampled edge is at least one foot into rough.
    for (let attempt = 0; attempt < 10; attempt++) {
      const clearance = minimumOutlineClearance(outer, targetFt, roughRadius);
      if (clearance >= 1) break;
      bunker.x += side * Math.max(0.25, 1.05 - clearance);
      outer = bunkerOutline(bunker, targetFt, 1);
    }

    result.push({
      ...bunker,
      centerZ,
      outer,
      wallTop: bunkerOutline(bunker, targetFt, 0.78),
      floor: bunkerOutline(bunker, targetFt, 0.65),
    });
  }
  return result;
}

export function realisticBunkerOpeningField(
  bunker: ResolvedRealisticBunker,
  x: number,
  z: number
): number {
  return cedarBunkerOpeningField(bunker, x, z);
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function distanceToSegment(
  x: number,
  z: number,
  startX: number,
  startZ: number,
  endX: number,
  endZ: number
): number {
  const dx = endX - startX;
  const dz = endZ - startZ;
  const lengthSquared = dx * dx + dz * dz;
  const amount =
    lengthSquared > 0
      ? Math.max(0, Math.min(1, ((x - startX) * dx + (z - startZ) * dz) / lengthSquared))
      : 0;
  return Math.hypot(x - (startX + dx * amount), z - (startZ + dz * amount));
}

function pointIsInsidePolygon(
  points: ReadonlyArray<readonly [number, number]>,
  x: number,
  z: number
): boolean {
  let inside = false;
  for (
    let current = 0, previous = points.length - 1;
    current < points.length;
    previous = current++
  ) {
    const [currentX, currentZ] = points[current];
    const [previousX, previousZ] = points[previous];
    if (
      currentZ > z !== previousZ > z &&
      x < ((previousX - currentX) * (z - currentZ)) / (previousZ - currentZ) + currentX
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function distanceOutsidePolygon(
  points: ReadonlyArray<readonly [number, number]>,
  x: number,
  z: number
): number {
  if (pointIsInsidePolygon(points, x, z)) return 0;
  let closest = Number.POSITIVE_INFINITY;
  for (let current = 0; current < points.length; current++) {
    const next = (current + 1) % points.length;
    closest = Math.min(
      closest,
      distanceToSegment(x, z, ...points[current], ...points[next])
    );
  }
  return closest;
}

function unitHash(seed: number, hole: number, lobe: number, channel: number): number {
  let value =
    seed ^
    Math.imul(hole + 1, 0x9e3779b1) ^
    Math.imul(lobe + 3, 0x85ebca6b) ^
    Math.imul(channel + 7, 0xc2b2ae35);
  value = Math.imul(value ^ (value >>> 16), 0x7feb352d);
  value = Math.imul(value ^ (value >>> 15), 0x846ca68b);
  return ((value ^ (value >>> 16)) >>> 0) / 0x100000000;
}

export function realisticTerrainHeightAt(
  sceneryId: RealisticSceneryId,
  holeNumber: number,
  targetFt: number,
  bunkers: readonly ResolvedRealisticBunker[],
  x: number,
  z: number,
  additionalFlatPath?: RealisticFlatPath
): number {
  if (sceneryId === 'cedar') {
    return cedarTerrainHeightAt(
      holeNumber,
      targetFt,
      bunkers,
      x,
      z,
      additionalFlatPath
    );
  }

  const spec = realisticSpecById(sceneryId);
  const terrain = spec.terrain;
  const hole = clampedHole(holeNumber);
  const greenRadius = realisticGreenRadiusForHole(sceneryId, hole, targetFt);
  const distanceFromGreen = realisticGreenZoneDistance(x, z, targetFt);
  const centerZ = -targetFt * 0.55;
  const localEdge = Math.max(Math.abs(x), Math.abs(z - centerZ));
  const edgeFade = 1 - smoothstep(terrain.edgeFadeStartFt, terrain.edgeFadeEndFt, localEdge);
  if (edgeFade <= 0) return 0;

  // Keep the complete playing surface and a mesh-safe rough collar level.
  let protection = smoothstep(
    greenRadius + 15,
    greenRadius + terrain.fullReliefOffsetFt,
    distanceFromGreen
  );
  protection = Math.min(
    protection,
    smoothstep(4, 18, distanceToSegment(x, z, 0, 0, 0, -targetFt))
  );
  if (additionalFlatPath) {
    protection = Math.min(
      protection,
      smoothstep(
        4,
        18,
        distanceToSegment(
          x,
          z,
          additionalFlatPath.startX,
          additionalFlatPath.startZ,
          additionalFlatPath.endX,
          additionalFlatPath.endZ
        )
      )
    );
  }
  for (const bunker of bunkers) {
    // Most terrain vertices are hundreds of feet from each small hazard. A
    // conservative circle rejects them before the 18-edge polygon distance
    // walk; outside this radius that walk can only return a fully faded mask.
    const deltaX = x - bunker.x;
    const deltaZ = z - bunker.centerZ;
    const influenceRadius = Math.hypot(bunker.width, bunker.length) * 0.66 + 33;
    if (deltaX * deltaX + deltaZ * deltaZ > influenceRadius * influenceRadius) {
      continue;
    }
    protection = Math.min(
      protection,
      smoothstep(9, 33, distanceOutsidePolygon(bunker.outer, x, z))
    );
  }
  if (protection <= 1e-9) return 0;

  const phase = hole * 1.61803398875 + unitHash(spec.seed, hole, 0, 0) * Math.PI * 2;
  let relief = 0;
  for (let lobe = 0; lobe < terrain.lobeCount; lobe++) {
    const angle =
      phase +
      lobe * 2.39996322973 +
      (unitHash(spec.seed, hole, lobe, 1) - 0.5) * 0.42;
    const radius =
      greenRadius +
      terrain.radialOffsetFt +
      unitHash(spec.seed, hole, lobe, 2) * terrain.radialJitterFt;
    const lobeX = Math.cos(angle) * radius;
    const lobeZ = centerZ + Math.sin(angle) * radius * 0.9;
    const spreadX =
      terrain.spreadXBaseFt +
      unitHash(spec.seed, hole, lobe, 3) * terrain.spreadXJitterFt;
    const spreadZ =
      terrain.spreadZBaseFt +
      unitHash(spec.seed, hole, lobe, 4) * terrain.spreadZJitterFt;
    const normalized =
      ((x - lobeX) * (x - lobeX)) / (spreadX * spreadX) +
      ((z - lobeZ) * (z - lobeZ)) / (spreadZ * spreadZ);
    const amplitude =
      lobe === (hole + spec.seed) % terrain.lobeCount
        ? -terrain.depressionBaseFt
        : terrain.riseBaseFt +
          unitHash(spec.seed, hole, lobe, 5) * terrain.riseJitterFt;
    relief += amplitude * Math.exp(-0.5 * normalized);
  }

  return (
    Math.max(-terrain.maxDepressionFt, Math.min(terrain.maxRiseFt, relief)) *
    protection *
    edgeFade
  );
}

function terrainCellIndex(coordinates: readonly number[], value: number): number {
  if (value < coordinates[0] || value > coordinates[coordinates.length - 1]) return -1;
  let low = 0;
  let high = coordinates.length - 1;
  while (low + 1 < high) {
    const middle = Math.floor((low + high) / 2);
    if (coordinates[middle] <= value) low = middle;
    else high = middle;
  }
  return Math.min(low, coordinates.length - 2);
}

export function realisticRenderedTerrainHeightAt(
  sceneryId: RealisticSceneryId,
  holeNumber: number,
  targetFt: number,
  bunkers: readonly ResolvedRealisticBunker[],
  x: number,
  z: number,
  additionalFlatPath?: RealisticFlatPath
): number {
  if (sceneryId === 'cedar') {
    return cedarRenderedTerrainHeightAt(
      holeNumber,
      targetFt,
      bunkers,
      x,
      z,
      additionalFlatPath
    );
  }

  const xCell = terrainCellIndex(CEDAR_TERRAIN_X_COORDS, x);
  const zCell = terrainCellIndex(CEDAR_TERRAIN_Z_COORDS, z);
  if (xCell < 0 || zCell < 0) return 0;

  const x0 = CEDAR_TERRAIN_X_COORDS[xCell];
  const x1 = CEDAR_TERRAIN_X_COORDS[xCell + 1];
  const z0 = CEDAR_TERRAIN_Z_COORDS[zCell];
  const z1 = CEDAR_TERRAIN_Z_COORDS[zCell + 1];
  const across = (x - x0) / (x1 - x0);
  const down = (z - z0) / (z1 - z0);
  const heightAt = (sampleX: number, sampleZ: number) =>
    realisticTerrainHeightAt(
      sceneryId,
      holeNumber,
      targetFt,
      bunkers,
      sampleX,
      sampleZ,
      additionalFlatPath
    );
  const nearLeft = heightAt(x0, z0);
  const nearRight = heightAt(x1, z0);
  const farLeft = heightAt(x0, z1);
  const farRight = heightAt(x1, z1);

  if (across + down <= 1) {
    return nearLeft * (1 - across - down) + nearRight * across + farLeft * down;
  }
  return nearRight * (1 - down) + farLeft * (1 - across) + farRight * (across + down - 1);
}

export function realisticVegetationSpecsForHole(
  sceneryId: RealisticSceneryId,
  holeNumber: number
): RealisticVegetationSpec[] {
  if (sceneryId === 'cedar') return cedarTreeSpecsForHole(holeNumber);

  const spec = realisticSpecById(sceneryId);
  const vegetation = spec.vegetation;
  const hole = clampedHole(holeNumber);
  const random = seededRandom(spec.seed ^ Math.imul(hole, 7919));
  const cards: RealisticVegetationSpec[] = [];
  const makeCard = (x: number, z: number, index: number): RealisticVegetationSpec => {
    const variant = (index + hole + (spec.seed & 3)) % 4;
    const height = vegetation.minHeightFt + random() * vegetation.heightJitterFt;
    return {
      x,
      z,
      height,
      width: height * vegetation.variantWidthRatios[variant] * (0.94 + random() * 0.12),
      variant,
      tone: (random() - 0.5) * vegetation.toneJitter,
    };
  };

  for (const side of [-1, 1]) {
    for (let index = 0; index < vegetation.nearCardsPerSide; index++) {
      const x =
        side *
        (vegetation.nearMinXFt +
          random() * vegetation.nearXJitterFt +
          index * 1.15);
      const z =
        vegetation.nearStartZFt -
        index * vegetation.nearStepZFt -
        random() * vegetation.nearStepZFt * 0.65;
      cards.push(makeCard(x, z, cards.length));
    }
  }

  const horizonCount = vegetation.cardCount - cards.length;
  for (let index = 0; index < horizonCount; index++) {
    const amount = horizonCount <= 1 ? 0.5 : index / (horizonCount - 1);
    const angle = -1.03 + amount * 2.06 + (random() - 0.5) * 0.06;
    const radius =
      vegetation.horizonRadiusFt +
      (index % 2) * vegetation.horizonDepthJitterFt * 0.34 +
      random() * vegetation.horizonDepthJitterFt;
    let x = Math.sin(angle) * radius + (random() - 0.5) * 7;
    if (Math.abs(x) < vegetation.corridorHalfWidthFt) {
      x = (x < 0 ? -1 : 1) * (vegetation.corridorHalfWidthFt + random() * 8);
    }
    const z = -Math.cos(angle) * radius + (random() - 0.5) * 6;
    cards.push(makeCard(x, z, cards.length));
  }

  return cards;
}
